import { LocalOffer } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
    couponAppliedContainerStyleMobile,
    couponAppliedContainerStyle,
    couponFieldStyle,
} from "./CouponStyles";
import {
    CircularProgress,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme as useMuiTheme,
} from "@mui/material";
import { useTheme } from "../../util/themes";
import { CouponStatus } from "./hooks/useCheckoutForm";
import { AppliedCouponDiscountFields } from "../../types/purchaseOrder/couponTypes";
import { TrashIcon } from "@phosphor-icons/react";
import React from "react";
import { CHECKOUT_MAXLEN } from "../../utils/checkoutFieldMaxLengths";

export const CouponSection: React.FC<{
    showCouponField: boolean;
    couponCode: string;
    onShowCouponField: () => void;
    onCouponCodeChange: (value: string) => void;
    onCouponSubmit: (name: string) => void;
    onCouponDelete: () => void;
    couponStatus: CouponStatus;
}> = ({
    showCouponField,
    couponCode,
    onShowCouponField,
    onCouponCodeChange,
    onCouponSubmit,
    onCouponDelete,
    couponStatus,
}) => {
    const muiTheme = useMuiTheme();
    const customTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

    const appliedDiscount = couponStatus.appliedDiscount;

    const hasAppliedCoupon = !couponStatus.loading && !!appliedDiscount && !couponStatus.error;

    // 1) Si ya hay cupón aplicado, SIEMPRE muestro el "chip" con eliminar
    if (hasAppliedCoupon && appliedDiscount) {
        return (
            <Box sx={isMobile ? couponAppliedContainerStyleMobile : couponAppliedContainerStyle}>
                <Stack
                    direction={"row"}
                    spacing={2}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        textTransform={"uppercase"}
                        fontFamily={"inherit"}
                        fontSize={"8px"}
                        sx={{ color: customTheme.colors.text.primary, opacity: 0.8 }}
                    >
                        {`Cupón: ${appliedDiscount[AppliedCouponDiscountFields.Name]}`}
                    </Typography>

                    <IconButton
                        size={"small"}
                        onClick={onCouponDelete}
                        sx={{
                            color: customTheme.colors.text.primary,
                            "&:hover": {
                                color: customTheme.colors.text.secondary,
                                opacity: 0.8,
                            },
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Stack>
            </Box>
        );
    }

    // 2) Si no está aplicado y todavía no abrió el campo, muestro el CTA
    if (!showCouponField) {
        return (
            <Button
                variant="outlined"
                startIcon={<LocalOffer />}
                fullWidth
                onClick={onShowCouponField}
                sx={{
                    mt: 2,
                    borderRadius: 0,
                    borderColor: customTheme.colors.greyLight,
                    color: customTheme.colors.text.primary,
                    fontFamily: "inherit",
                    fontWeight: "400",
                    textTransform: "none",
                    "&:hover": {
                        borderColor: customTheme.colors.greyLight,
                        backgroundColor: customTheme.colors.background.paper,
                        color: customTheme.colors.text.secondary,
                    },
                }}
            >
                Agregar cupón de descuento
            </Button>
        );
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (couponCode.trim()) onCouponSubmit(couponCode);
    };

    // 3) Campo para ingresar cupón
    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Stack spacing={2}>
                <TextField
                    fullWidth
                    label="¿Tenés un cupón de descuento?"
                    variant="outlined"
                    value={couponCode}
                    onChange={(e) =>
                        onCouponCodeChange(e.target.value.slice(0, CHECKOUT_MAXLEN.vchNombreCupon))
                    }
                    sx={couponFieldStyle}
                    disabled={couponStatus.loading}
                    inputProps={{ maxLength: CHECKOUT_MAXLEN.vchNombreCupon }}
                />

                <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    color="tertiary"
                    disabled={couponStatus.loading || !couponCode.trim()}
                    startIcon={
                        couponStatus.loading ? <CircularProgress size={20} color="inherit" /> : null
                    }
                >
                    {couponStatus.loading ? "APLICANDO..." : "APLICAR DESCUENTO"}
                </Button>

                {couponStatus.success && !couponStatus.loading && (
                    <Typography
                        variant="body2"
                        sx={{ color: "success.main", fontWeight: 500, textAlign: "center" }}
                    >
                        Cupón aplicado correctamente
                    </Typography>
                )}

                {couponStatus.error && !couponStatus.loading && (
                    <Typography
                        variant="body2"
                        sx={{ color: "error.main", fontWeight: 500, textAlign: "center" }}
                    >
                        {couponStatus.error}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};
